/**
 * Meta's Embedded Signup, wrapped in one promise.
 *
 * This is the only way to onboard a WhatsApp number that stays live in the
 * WhatsApp Business app on somebody's phone — what Meta calls Coexistence. It
 * cannot be reached by pasting a token: the flow has to run in a Meta-hosted
 * window that verifies the number by QR from inside the app and asks the owner
 * to share their history.
 *
 * Two halves arrive by different routes and both are required:
 *
 *   • the **asset ids** — the account and the number — arrive as a `message`
 *     event posted from the popup, which Meta calls session logging;
 *   • the **exchangeable code** arrives in the `FB.login` callback.
 *
 * Neither is any use alone, and they can land in either order, so this resolves
 * only once both are in hand. The code expires in **thirty seconds**, which is
 * why the caller sends it straight on rather than storing it anywhere.
 *
 * Verified against Meta's docs on 2026-08-24:
 * https://developers.facebook.com/documentation/business-messaging/whatsapp/embedded-signup/implementation/
 */

export interface SignupResult {
  code: string;
  wabaId: string;
  phoneNumberId: string;
}

interface FacebookSdk {
  init: (options: {
    appId: string;
    autoLogAppEvents: boolean;
    xfbml: boolean;
    version: string;
  }) => void;
  login: (
    callback: (response: { authResponse?: { code?: string } | null }) => void,
    options: Record<string, unknown>,
  ) => void;
}

declare global {
  interface Window {
    FB?: FacebookSdk;
    fbAsyncInit?: () => void;
  }
}

/** Pinned to the version the rest of this product speaks. */
const GRAPH_VERSION = 'v26.0';
const SDK_ID = 'facebook-jssdk';

let loading: Promise<FacebookSdk> | null = null;

/**
 * Load and initialise the Facebook JavaScript SDK, once.
 *
 * Kept out of `index.html` deliberately: it is a third-party script that only
 * one screen needs, and loading it on every page view would put a Meta request
 * in front of a dashboard that is mostly used without it.
 */
function loadSdk(appId: string): Promise<FacebookSdk> {
  if (window.FB) return Promise.resolve(window.FB);
  if (loading) return loading;

  loading = new Promise<FacebookSdk>((resolve, reject) => {
    window.fbAsyncInit = () => {
      if (!window.FB) {
        reject(new Error('The Facebook SDK loaded but did not initialise'));
        return;
      }
      window.FB.init({
        appId,
        autoLogAppEvents: true,
        xfbml: false,
        version: GRAPH_VERSION,
      });
      resolve(window.FB);
    };

    if (document.getElementById(SDK_ID)) return;

    const script = document.createElement('script');
    script.id = SDK_ID;
    script.src = 'https://connect.facebook.net/en_US/sdk.js';
    script.async = true;
    script.defer = true;
    script.crossOrigin = 'anonymous';
    // A blocked script never calls fbAsyncInit, so the promise would hang for
    // ever. An ad blocker is the ordinary cause and worth naming.
    script.onerror = () =>
      reject(new Error('The Facebook SDK could not be loaded. A blocker may be stopping it.'));
    document.body.appendChild(script);
  });

  return loading;
}

/**
 * Run the flow and resolve with everything the server needs.
 *
 * Rejects if the person closes the window before the final screen, or if either
 * half fails to arrive. Meta treats closing the window *on* the final screen as
 * success and still sends both halves, so that case resolves normally.
 */
export async function runEmbeddedSignup(params: {
  appId: string;
  configId: string;
}): Promise<SignupResult> {
  const FB = await loadSdk(params.appId);

  return new Promise<SignupResult>((resolve, reject) => {
    let assets: { wabaId: string; phoneNumberId: string } | null = null;
    let code: string | null = null;
    let settled = false;

    const finish = () => {
      if (settled || !assets || !code) return;
      settled = true;
      window.removeEventListener('message', onMessage);
      resolve({ code, ...assets });
    };

    const fail = (message: string) => {
      if (settled) return;
      settled = true;
      window.removeEventListener('message', onMessage);
      reject(new Error(message));
    };

    const onMessage = (event: MessageEvent) => {
      // Anything not from Meta is somebody else's message on the same page.
      if (!event.origin.endsWith('facebook.com')) return;

      try {
        const payload = JSON.parse(String(event.data)) as {
          type?: string;
          event?: string;
          data?: { waba_id?: string; phone_number_id?: string; current_step?: string };
        };
        if (payload.type !== 'WA_EMBEDDED_SIGNUP') return;

        if (payload.data?.waba_id && payload.data.phone_number_id) {
          assets = {
            wabaId: payload.data.waba_id,
            phoneNumberId: payload.data.phone_number_id,
          };
          finish();
          return;
        }

        // Abandoned: Meta names the screen they stopped on, which is the most
        // useful thing anyone can be told about a flow that did not finish.
        if (payload.event === 'CANCEL' && payload.data?.current_step) {
          fail(`تُركت نافذة التسجيل عند خطوة: ${payload.data.current_step}`);
        }
      } catch {
        // Meta posts other, unparseable messages through the same channel.
      }
    };

    window.addEventListener('message', onMessage);

    FB.login(
      (response) => {
        const returned = response.authResponse?.code;
        if (!returned) {
          fail('لم تكتمل نافذة التسجيل');
          return;
        }
        code = returned;
        finish();
      },
      {
        config_id: params.configId,
        response_type: 'code',
        override_default_response_type: true,
        extras: { setup: {} },
      },
    );

    /*
     * A ceiling on the wait.
     *
     * The flow itself has no time limit — somebody may spend minutes scanning a
     * QR code from their phone — but if one half never arrives the promise would
     * otherwise never settle and the button would spin for ever.
     */
    window.setTimeout(() => fail('انتهت مهلة نافذة التسجيل'), 15 * 60 * 1000);
  });
}
